import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ZoomableImage } from './ZoomableImage';
import { ScrollReveal } from './ScrollReveal';

const OVERVIEW_SLIDES = [
  {
    src: '/images/screenshots/noteon-overview-full-width-en.jpeg',
    alt: 'Noteon app overview',
  },
  {
    src: '/images/screenshots/noteon-start-recording-en.jpeg',
    alt: 'Start Recording screen',
  },
  {
    src: '/images/screenshots/noteon-final-transcription-en.jpeg',
    alt: 'Final transcription screen',
  },
  {
    src: '/images/screenshots/noteon-session-archive-en.jpeg',
    alt: 'Session archive screen',
  },
];

const AUTOPLAY_MS = 4200;

interface AppShowcaseProps {
  showSteps?: boolean;
}

export function AppShowcase({ showSteps = true }: AppShowcaseProps) {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);

  const steps = [
    {
      badge: t('showcase.step1.badge'),
      title: t('showcase.step1.title'),
      description: t('showcase.step1.description'),
      features: [
        t('showcase.step1.feature1'),
        t('showcase.step1.feature2'),
        t('showcase.step1.feature3'),
      ],
      image: '/images/screenshots/noteon-start-recording-en.jpeg',
      imageAlt: 'Start Recording screen',
      reverse: false,
    },
    {
      badge: t('showcase.step2.badge'),
      title: t('showcase.step2.title'),
      description: t('showcase.step2.description'),
      features: [
        t('showcase.step2.feature1'),
        t('showcase.step2.feature2'),
        t('showcase.step2.feature3'),
      ],
      image: '/images/screenshots/noteon-final-transcription-en.jpeg',
      imageAlt: 'Final transcription screen',
      reverse: true,
    },
    {
      badge: t('showcase.step3.badge'),
      title: t('showcase.step3.title'),
      description: t('showcase.step3.description'),
      features: [
        t('showcase.step3.feature1'),
        t('showcase.step3.feature2'),
        t('showcase.step3.feature3'),
      ],
      image: '/images/screenshots/noteon-session-archive-en.jpeg',
      imageAlt: 'Session archive screen',
      reverse: false,
    },
  ];

  const totalSlides = OVERVIEW_SLIDES.length;

  useEffect(() => {
    if (totalSlides < 2 || isAutoplayPaused || typeof window === 'undefined') return;

    const intervalId = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % totalSlides);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(intervalId);
  }, [isAutoplayPaused, totalSlides]);

  const goToSlide = (index: number) => {
    if (totalSlides < 1) return;
    const normalizedIndex = ((index % totalSlides) + totalSlides) % totalSlides;
    setActiveSlide(normalizedIndex);
  };

  return (
    <>
      <section id="overview-screenshot" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <ScrollReveal threshold={0.01} rootMargin="0px 0px 24% 0px">
              <div
                onMouseEnter={() => setIsAutoplayPaused(true)}
                onMouseLeave={() => setIsAutoplayPaused(false)}
                onFocusCapture={() => setIsAutoplayPaused(true)}
                onBlurCapture={() => setIsAutoplayPaused(false)}
                style={{ position: 'relative' }}
              >
                <div
                  style={{
                    border: '1px solid rgb(229, 231, 235)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 36px rgba(15, 23, 42, 0.12)',
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      transform: `translateX(-${activeSlide * 100}%)`,
                      transition: 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)',
                    }}
                  >
                    {OVERVIEW_SLIDES.map((slide) => (
                      <div
                        key={slide.src}
                        style={{
                          flex: '0 0 100%',
                          minWidth: 0,
                        }}
                      >
                        <ZoomableImage
                          src={slide.src}
                          alt={slide.alt}
                          imageClassName="w-full h-auto"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  aria-label="Previous screenshot"
                  onClick={() => goToSlide(activeSlide - 1)}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '12px',
                    transform: 'translateY(-50%)',
                    width: '40px',
                    height: '40px',
                    border: '1px solid rgba(17, 24, 39, 0.14)',
                    borderRadius: '999px',
                    background: 'rgba(255, 255, 255, 0.92)',
                    color: '#111827',
                    fontSize: '22px',
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  &#8249;
                </button>

                <button
                  type="button"
                  aria-label="Next screenshot"
                  onClick={() => goToSlide(activeSlide + 1)}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '12px',
                    transform: 'translateY(-50%)',
                    width: '40px',
                    height: '40px',
                    border: '1px solid rgba(17, 24, 39, 0.14)',
                    borderRadius: '999px',
                    background: 'rgba(255, 255, 255, 0.92)',
                    color: '#111827',
                    fontSize: '22px',
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  &#8250;
                </button>

                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: '16px',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '8px',
                    background: 'rgba(17, 24, 39, 0.38)',
                    borderRadius: '999px',
                    padding: '8px 10px',
                  }}
                >
                  {OVERVIEW_SLIDES.map((slide, index) => (
                    <button
                      key={slide.src}
                      type="button"
                      aria-label={`Go to screenshot ${index + 1}`}
                      onClick={() => goToSlide(index)}
                      style={{
                        width: index === activeSlide ? '22px' : '8px',
                        height: '8px',
                        border: '0',
                        borderRadius: '999px',
                        background: index === activeSlide ? '#ffffff' : 'rgba(255, 255, 255, 0.45)',
                        cursor: 'pointer',
                        transition: 'all 160ms ease',
                      }}
                    />
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {showSteps ? (
        <section id="showcase" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <ScrollReveal>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('showcase.title')}
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <p className="text-lg text-gray-600">
                {t('showcase.subtitle')}
              </p>
            </ScrollReveal>
          </div>

          <div className="space-y-20">
            {steps.map((step, index) => (
              <div key={index} className="grid lg:grid-cols-2 gap-12 items-center">
                <div className={step.reverse ? 'order-1' : 'order-2 lg:order-1'}>
                  <ScrollReveal delay={1}>
                    <ZoomableImage
                      src={step.image}
                      alt={step.imageAlt}
                      frameClassName="rounded-2xl overflow-hidden shadow-2xl border border-gray-200"
                      imageClassName="w-full h-auto"
                    />
                  </ScrollReveal>
                </div>
                
                <div className={step.reverse ? 'order-2' : 'order-1 lg:order-2'}>
                  <ScrollReveal delay={2}>
                    <div className="inline-block bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                      {step.badge}
                    </div>
                  </ScrollReveal>
                  <ScrollReveal delay={3}>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                  </ScrollReveal>
                  <ScrollReveal delay={4}>
                    <p className="text-lg text-gray-600 mb-6">
                      {step.description}
                    </p>
                  </ScrollReveal>
                  <ScrollReveal delay={5}>
                    <ul className="space-y-3">
                      {step.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="bg-teal-100 rounded-full p-1 mt-1">
                            <svg className="size-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </ScrollReveal>
                </div>
              </div>
            ))}
          </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
